import { useMutation } from '@tanstack/react-query';
import { deleteQuoteV2SubitemMutation } from 'Services/Mutations/QuotesV2';
import { removeFromItems } from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface DeleteSubitemVariables {
  itemId: string;
  subItemId: string;
}

/**
 * Delete a sub-item (option/accessory) from a parent product via the
 * dedicated v2 sub-item endpoint. Applies optimistic removal immediately
 * (including clearing parent labelType when last child is removed),
 * reverts on error, and invalidates on success.
 */
export const useDeleteSubitem = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = deleteQuoteV2SubitemMutation();

  return useMutation({
    mutationFn: ({ itemId, subItemId }: DeleteSubitemVariables) =>
      mutationFn({ quoteId, itemId, subId: subItemId }),

    onMutate: async ({ subItemId }) => {
      await cache.cancel();

      const previousItems = cache.getItems();

      cache.setItems(old => {
        if (!old) return old;
        return removeFromItems(old, subItemId);
      });

      return { previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: () => {
      cache.invalidate();
    }
  });
};
